import * as THREE from 'three';

/**
 * Jewellery: rings, earrings, chains, beads, bangles, watches, waist beads, a nose stud. Each piece is made in code
 * and fitted to the person wearing it: it wraps the body (and whatever they're wearing there) at its spot, so a chain
 * sits on a collar or on skin, a bangle round this wrist, a ring round this finger, then it rides the bone under it.
 * Kept apart from the outfit's accessories (Appearance.jewellery): changing clothes never takes your rings off.
 */

export type JewelKind = 'studs' | 'hoops' | 'drops' | 'chain' | 'cuban' | 'pendant' | 'pearls' | 'coral' | 'choker' | 'bangles' | 'beads' | 'watch' | 'ring' | 'signet' | 'solitaire' | 'stack' | 'waistbeads' | 'nosestud';
export type JewelSlot = 'ears' | 'neck' | 'wrist' | 'hand' | 'waist' | 'nose';
export type Metal = 'gold' | 'silver' | 'rose';
export interface JewelDef {
  id: string; kind: JewelKind; label: string; price: number;
  metal?: Metal;
  /** the beads' or the stone's colours (coral, pearls, glass beads), else the metal's */
  colours?: number[];
  /** (a hint for the creator and the shops; anyone can wear anything) */
  for?: 'm' | 'f';
  note: string;
}

export const SLOT_OF: Record<JewelKind, JewelSlot> = {
  studs: 'ears', hoops: 'ears', drops: 'ears', chain: 'neck', cuban: 'neck', pendant: 'neck', pearls: 'neck', coral: 'neck', choker: 'neck',
  bangles: 'wrist', beads: 'wrist', watch: 'wrist', ring: 'hand', signet: 'hand', solitaire: 'hand', stack: 'hand', waistbeads: 'waist', nosestud: 'nose',
};
/** how many of a slot can be worn at once (two necklaces layered, a bracelet on each wrist, rings on both hands) */
export const SLOT_MAX: Record<JewelSlot, number> = { ears: 1, neck: 2, wrist: 2, hand: 2, waist: 1, nose: 1 };
export const SLOT_LABEL: Record<JewelSlot, string> = { ears: 'Earrings', neck: 'Necklaces', wrist: 'Wrist', hand: 'Rings', waist: 'Waist beads', nose: 'Nose' };

const CORAL = [0xc8381f, 0xd2452a, 0xb83018], PEARL = [0xf3eee4], AJ = [0xe8b030, 0x1f7a4a, 0xc8381f, 0x1d1d1d, 0xf0ece0];
export const JEWELLERY: JewelDef[] = [
  // ears
  { id: 'studs-gold', kind: 'studs', label: 'Gold studs', price: 18_000, metal: 'gold', note: 'Small and always right' },
  { id: 'studs-diamond', kind: 'studs', label: 'Diamond studs', price: 240_000, metal: 'silver', colours: [0xf4f8ff], note: 'They catch every light' },
  { id: 'studs-silver', kind: 'studs', label: 'Silver studs', price: 9_000, metal: 'silver', note: 'Clean, quiet' },
  { id: 'hoops-gold-s', kind: 'hoops', label: 'Small gold hoops', price: 22_000, metal: 'gold', for: 'f', note: 'Everyday hoops' },
  { id: 'hoops-gold-l', kind: 'hoops', label: 'Big gold hoops', price: 34_000, metal: 'gold', for: 'f', note: 'Statement, loud' },
  { id: 'hoops-silver', kind: 'hoops', label: 'Silver hoops', price: 15_000, metal: 'silver', for: 'f', note: 'Cool-toned' },
  { id: 'drops-coral', kind: 'drops', label: 'Coral drops', price: 28_000, metal: 'gold', colours: CORAL, for: 'f', note: 'Owambe ready' },
  { id: 'drops-pearl', kind: 'drops', label: 'Pearl drops', price: 32_000, metal: 'gold', colours: PEARL, for: 'f', note: 'Soft and classic' },
  // neck
  { id: 'chain-gold', kind: 'chain', label: 'Thin gold chain', price: 45_000, metal: 'gold', note: 'Goes with anything' },
  { id: 'chain-silver', kind: 'chain', label: 'Silver chain', price: 20_000, metal: 'silver', note: 'Minimal' },
  { id: 'cuban-gold', kind: 'cuban', label: 'Cuban link (gold)', price: 380_000, metal: 'gold', for: 'm', note: 'Heavy. People notice.' },
  { id: 'cuban-silver', kind: 'cuban', label: 'Cuban link (silver)', price: 160_000, metal: 'silver', for: 'm', note: 'Heavy, cool-toned' },
  { id: 'pendant-gold', kind: 'pendant', label: 'Gold pendant', price: 65_000, metal: 'gold', note: 'A little charm on a chain' },
  { id: 'pearls', kind: 'pearls', label: 'Pearl strand', price: 90_000, colours: PEARL, metal: 'gold', for: 'f', note: 'Old money in one piece' },
  { id: 'coral-3', kind: 'coral', label: 'Coral beads (3 strands)', price: 120_000, colours: CORAL, metal: 'gold', note: 'For the wedding, the chieftaincy, the owambe' },
  { id: 'choker-gold', kind: 'choker', label: 'Gold choker', price: 38_000, metal: 'gold', for: 'f', note: 'Close at the throat' },
  { id: 'choker-silver', kind: 'choker', label: 'Silver choker', price: 22_000, metal: 'silver', note: 'Goth staple, tight at the throat' },
  // wrists
  { id: 'watch-gold', kind: 'watch', label: 'Gold watch', price: 450_000, metal: 'gold', note: 'Time is money. This is both.' },
  { id: 'watch-steel', kind: 'watch', label: 'Steel watch', price: 120_000, metal: 'silver', note: 'Quietly expensive' },
  { id: 'watch-leather', kind: 'watch', label: 'Leather-strap watch', price: 55_000, metal: 'gold', colours: [0x4a2a16], note: 'Warm and classic' },
  { id: 'bangles-gold', kind: 'bangles', label: 'Gold bangles (stack)', price: 60_000, metal: 'gold', for: 'f', note: 'They jingle when you wave' },
  { id: 'bangles-silver', kind: 'bangles', label: 'Silver bangles', price: 26_000, metal: 'silver', for: 'f', note: 'A light stack' },
  { id: 'beads-wood', kind: 'beads', label: 'Wooden bead bracelet', price: 6_000, colours: [0x7a4a26, 0x5a3418], note: 'Earthy, handmade' },
  { id: 'beads-aj', kind: 'beads', label: 'Glass bead bracelet', price: 8_000, colours: AJ, note: 'From the market, all colours' },
  { id: 'beads-coral', kind: 'beads', label: 'Coral bead bracelet', price: 30_000, colours: CORAL, note: 'Matches the necklace' },
  // hands
  { id: 'ring-gold', kind: 'ring', label: 'Gold band', price: 35_000, metal: 'gold', note: 'Simple, solid' },
  { id: 'ring-silver', kind: 'ring', label: 'Silver band', price: 12_000, metal: 'silver', note: 'Simple, cool' },
  { id: 'signet-gold', kind: 'signet', label: 'Gold signet', price: 85_000, metal: 'gold', for: 'm', note: 'Family money, or looks it' },
  { id: 'solitaire', kind: 'solitaire', label: 'Diamond solitaire', price: 900_000, metal: 'silver', colours: [0xf4f8ff], for: 'f', note: 'Somebody said yes' },
  { id: 'stack-gold', kind: 'stack', label: 'Stacked gold rings', price: 48_000, metal: 'gold', for: 'f', note: 'Three thin bands' },
  { id: 'stack-silver', kind: 'stack', label: 'Stacked silver rings', price: 26_000, metal: 'silver', note: 'Three silver bands, a little dark' },
  // waist, nose
  { id: 'waist-coral', kind: 'waistbeads', label: 'Waist beads (coral)', price: 9_000, colours: CORAL, for: 'f', note: 'Under the clothes, for you' },
  { id: 'waist-aj', kind: 'waistbeads', label: 'Waist beads (colours)', price: 7_000, colours: AJ, for: 'f', note: 'Under the clothes, for you' },
  { id: 'nose-gold', kind: 'nosestud', label: 'Gold nose stud', price: 14_000, metal: 'gold', note: 'A tiny glint' },
  { id: 'nose-silver', kind: 'nosestud', label: 'Silver nose stud', price: 8_000, metal: 'silver', note: 'A tiny glint' },
];
export const jewelById = (id: string) => JEWELLERY.find((j) => j.id === id);

/** Put on a piece: same-slot pieces past the slot's limit come off (the oldest first). Taking one off: toggle it again. */
export function toggleJewel(worn: string[], id: string): string[] {
  if (worn.includes(id)) return worn.filter((x) => x !== id);
  const d = jewelById(id);
  if (!d) return worn;
  const slot = SLOT_OF[d.kind], same = worn.filter((x) => { const j = jewelById(x); return j && SLOT_OF[j.kind] === slot; });
  const drop = new Set(same.slice(0, Math.max(0, same.length + 1 - SLOT_MAX[slot])));
  // (one watch only, and two necklaces of the same kind look like a mistake)
  for (const x of same) { const j = jewelById(x)!; if (j.kind === d.kind && (d.kind === 'watch' || slot === 'neck')) drop.add(x); }
  return [...worn.filter((x) => !drop.has(x)), id];
}

// --- materials ------------------------------------------------------------------------
const METAL: Record<Metal, number> = { gold: 0xd9b04a, silver: 0xd8dce2, rose: 0xdca08a };
const mats = new Map<string, THREE.MeshStandardMaterial>();
/** (shared, never disposed: a handful for the whole game. Lit scenes outdoors have little to reflect, so a touch of
 * glow keeps gold reading as gold) */
function metal(m: Metal): THREE.MeshStandardMaterial {
  const k = `m:${m}`;
  let x = mats.get(k);
  if (!x) mats.set(k, (x = new THREE.MeshStandardMaterial({ color: METAL[m], metalness: 0.75, roughness: 0.28, emissive: METAL[m], emissiveIntensity: 0.14 })));
  return x;
}
function bead(c: number, shine = 0.35): THREE.MeshStandardMaterial {
  const k = `b:${c}:${shine}`;
  let x = mats.get(k);
  if (!x) mats.set(k, (x = new THREE.MeshStandardMaterial({ color: c, roughness: shine, metalness: 0, emissive: c, emissiveIntensity: 0.06 })));
  return x;
}
const stone = () => { let x = mats.get('stone'); if (!x) mats.set('stone', (x = new THREE.MeshStandardMaterial({ color: 0xf4f8ff, metalness: 0.2, roughness: 0.05, emissive: 0xdde8ff, emissiveIntensity: 0.45, flatShading: true }))); return x; };

// --- fitting --------------------------------------------------------------------------
export interface Surface { pos: Float32Array; bone: Uint16Array }
export interface Fit { bones: THREE.Bone[]; inv: THREE.Matrix4[]; head: (name: string) => THREE.Vector3 | null; body: Surface; dressed: Surface; eye: THREE.Box3 | null }

/** the rest-pose surfaces (body alone, and body + clothes), bone heads, the eyes' box: read once per body */
export function fitOf(root: THREE.Object3D): Fit | null {
  if (root.userData.jewelFit) return root.userData.jewelFit as Fit;
  let skel: THREE.Skeleton | null = null;
  const body: number[] = [], bodyB: number[] = [], dressed: number[] = [], dressedB: number[] = [];
  let eye: THREE.Box3 | null = null;
  root.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    skel ??= m.skeleton;
    if (m.name.startsWith('eyes:')) { m.geometry.computeBoundingBox(); eye = m.geometry.boundingBox!.clone(); return; }
    const isBody = m.name === 'body', isClothes = m.name.startsWith('clothes:');
    if (!isBody && !isClothes) return;
    const p = m.geometry.getAttribute('position'), si = m.geometry.getAttribute('skinIndex'), sw = m.geometry.getAttribute('skinWeight');
    for (let i = 0; i < p.count; i++) {
      // the bone that carries this vertex most
      let b = si.getX(i), w = sw.getX(i);
      if (sw.getY(i) > w) { b = si.getY(i); w = sw.getY(i); }
      if (sw.getZ(i) > w) { b = si.getZ(i); w = sw.getZ(i); }
      if (sw.getW(i) > w) b = si.getW(i);
      dressed.push(p.getX(i), p.getY(i), p.getZ(i)); dressedB.push(b);
      if (isBody) { body.push(p.getX(i), p.getY(i), p.getZ(i)); bodyB.push(b); }
    }
  });
  if (!skel || !body.length) return null;
  const s = skel as THREE.Skeleton;
  const inv = s.boneInverses;
  const head = (name: string) => { const i = s.bones.findIndex((b) => b.name === name); return i < 0 ? null : new THREE.Vector3().setFromMatrixPosition(inv[i].clone().invert()); };
  const fit: Fit = {
    bones: s.bones, inv, head,
    body: { pos: new Float32Array(body), bone: new Uint16Array(bodyB) }, dressed: { pos: new Float32Array(dressed), bone: new Uint16Array(dressedB) }, eye,
  };
  root.userData.jewelFit = fit;
  return fit;
}

/**
 * A loop round a limb (or the neck, or the waist): the surface's outermost reach, angle by angle, in the plane through
 * C across `axis`, plus `pad`. Angles with nothing get their neighbours' reach.
 */
function wrap(s: Surface, C: THREE.Vector3, axis: THREE.Vector3, half: number, maxR: number, pad: number, bones?: Set<number>, n = 28): THREE.Vector3[] {
  const a = axis.clone().normalize();
  const u = new THREE.Vector3(1, 0, 0); if (Math.abs(u.dot(a)) > 0.8) u.set(0, 0, 1);
  u.addScaledVector(a, -u.dot(a)).normalize();
  const v = new THREE.Vector3().crossVectors(a, u);
  const r = new Float32Array(n).fill(-1), d = new THREE.Vector3();
  for (let i = 0; i < s.bone.length; i++) {
    if (bones && !bones.has(s.bone[i])) continue;
    d.set(s.pos[i * 3] - C.x, s.pos[i * 3 + 1] - C.y, s.pos[i * 3 + 2] - C.z);
    const h = d.dot(a);
    if (Math.abs(h) > half) continue;
    const x = d.dot(u), y = d.dot(v), rr = Math.hypot(x, y);
    if (rr > maxR) continue;
    const k = Math.floor(((Math.atan2(y, x) + Math.PI) / (Math.PI * 2)) * n) % n;
    r[k] = Math.max(r[k], rr);
  }
  const known = [...r].filter((x) => x > 0);
  if (!known.length) return [];
  for (let k = 0; k < n; k++) if (r[k] < 0) { // (fill from the nearest known on each side)
    let lo = 1, hi = 1;
    while (r[(k - lo + n) % n] < 0 && lo < n) lo++;
    while (r[(k + hi) % n] < 0 && hi < n) hi++;
    r[k] = (r[(k - lo + n) % n] * hi + r[(k + hi) % n] * lo) / (lo + hi);
  }
  const sm = r.map((_, k) => Math.max(r[k], (r[(k + n - 1) % n] + 2 * r[k] + r[(k + 1) % n]) / 4)); // (smoothed, never inside)
  return [...sm].map((rr, k) => { const t = (k + 0.5) / n * Math.PI * 2 - Math.PI; return C.clone().addScaledVector(u, Math.cos(t) * (rr + pad)).addScaledVector(v, Math.sin(t) * (rr + pad)); });
}

/**
 * A necklace's line: round the neck at the back, falling to `drop` below that at the front, riding the outside of
 * the surface (skin or clothes) the whole way. At the sides it may not reach past the neck (else it lands on the
 * shoulders); at the front it can reach the chest.
 */
function drape(s: Surface, C: THREE.Vector3, drop: number, pad: number, neckR: number, n = 32): THREE.Vector3[] {
  const r = new Float32Array(n).fill(-1);
  const yAt = (t: number) => C.y - drop * Math.pow((1 + Math.cos(t)) / 2, 1.6); // (t = 0: straight ahead, down the chest)
  const rMax = (t: number) => neckR * (1.35 + 1.3 * Math.max(0, Math.cos(t)) ** 2); // (wider only toward the chest)
  for (let i = 0; i < s.bone.length; i++) {
    const x = s.pos[i * 3] - C.x, z = s.pos[i * 3 + 2] - C.z, y = s.pos[i * 3 + 1];
    const t = Math.atan2(x, z), rr = Math.hypot(x, z);
    if (Math.abs(y - yAt(t)) > 0.008 || rr > rMax(t)) continue;
    const k = Math.floor(((t + Math.PI) / (Math.PI * 2)) * n) % n;
    r[k] = Math.max(r[k], rr);
  }
  if (r.every((x) => x < 0)) return [];
  for (let k = 0; k < n; k++) if (r[k] < 0) {
    let lo = 1, hi = 1;
    while (r[(k - lo + n) % n] < 0 && lo < n) lo++;
    while (r[(k + hi) % n] < 0 && hi < n) hi++;
    r[k] = (r[(k - lo + n) % n] * hi + r[(k + hi) % n] * lo) / (lo + hi);
  }
  const sm = [...r].map((_, k) => Math.max(r[k], (r[(k + n - 1) % n] + 2 * r[k] + r[(k + 1) % n]) / 4));
  return sm.map((rr, k) => { const t = (k + 0.5) / n * Math.PI * 2 - Math.PI; return new THREE.Vector3(C.x + Math.sin(t) * (rr + pad), yAt(t), C.z + Math.cos(t) * (rr + pad)); });
}

const tube = (pts: THREE.Vector3[], radius: number, radial = 6) => new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts, true), Math.max(24, pts.length * 3), radius, radial, true);

/** beads strung along a loop: one instanced mesh per colour */
function string(g: THREE.Group, pts: THREE.Vector3[], size: number, colours: number[], gap = 1.05) {
  const curve = new THREE.CatmullRomCurve3(pts, true), len = curve.getLength(), n = Math.max(8, Math.floor(len / (size * 2 * gap)));
  const geo = new THREE.SphereGeometry(size, 8, 6), at = curve.getSpacedPoints(n);
  const by = new Map<number, THREE.Vector3[]>();
  for (let i = 0; i < n; i++) { const c = colours[i % colours.length]; (by.get(c) ?? by.set(c, []).get(c)!).push(at[i]); }
  for (const [c, ps] of by) {
    const m = new THREE.InstancedMesh(geo, bead(c), ps.length), M = new THREE.Matrix4();
    ps.forEach((p, i) => m.setMatrixAt(i, M.makeTranslation(p.x, p.y, p.z)));
    g.add(m);
  }
}

/** the loop's lowest-front point (where a pendant hangs) */
const frontOf = (pts: THREE.Vector3[]) => pts.reduce((a, p) => (p.z - p.y * 0.5 > a.z - a.y * 0.5 ? p : a), pts[0]);

/**
 * The piece, built in the body's rest pose (metres), and the bone it rides. Chains are drawn as loops tilted down at
 * the front (they hang onto the chest), ear pieces at the lobes, rings round the ring finger.
 */
function build(d: JewelDef, f: Fit, nth: number): { g: THREE.Group; at: THREE.Vector3 } | null {
  const g = new THREE.Group(), mt = metal(d.metal ?? 'gold'), slot = SLOT_OF[d.kind];
  const add = (geo: THREE.BufferGeometry, m: THREE.Material = mt) => { const k = new THREE.Mesh(geo, m); g.add(k); return k; };
  const H = (n: string) => f.head(n);
  if (slot === 'neck') {
    const n1 = H('neck01'), n2 = H('neck02');
    if (!n1 || !n2) return null;
    // the neck's own width, where the necklace sits (a thin slab of skin round the neck bone, nothing wider)
    const C = n1.clone().lerp(n2, d.kind === 'choker' ? 0.75 : 0.15);
    let neckR = 0;
    { const b = f.body; for (let i = 0; i < b.bone.length; i++) { const dx = b.pos[i * 3] - C.x, dz = b.pos[i * 3 + 2] - C.z; if (Math.abs(b.pos[i * 3 + 1] - n2.y) < 0.01 && Math.hypot(dx, dz) < 0.09) neckR = Math.max(neckR, Math.hypot(dx, dz)); } }
    neckR = THREE.MathUtils.clamp(neckR || 0.055, 0.045, 0.075);
    // how far down the chest the front falls: a choker none, a chain onto the breastbone, a pendant lower
    const drop = { choker: 0.004, chain: 0.07, cuban: 0.095, pendant: 0.1, pearls: 0.075, coral: 0.055 }[d.kind as 'chain'] ?? 0.07;
    const thick = d.kind === 'cuban' ? 0.0045 : d.kind === 'choker' ? 0.003 : 0.0014;
    const second = nth ? 0.025 : 0; // (layered: the second necklace hangs lower)
    const pts = drape(f.dressed, C, drop + second, thick + 0.0015, neckR);
    if (pts.length < 8) return null;
    if (d.kind === 'pearls') string(g, pts, 0.0042, d.colours ?? PEARL, 1.0);
    else if (d.kind === 'coral') {
      for (let k = 0; k < 3; k++) { // three strands, each lower and looser at the front
        const ps = drape(f.dressed, C, drop + second + k * 0.018, 0.005 + k * 0.0015, neckR);
        if (ps.length >= 8) string(g, ps, 0.0046 - k * 0.0003, d.colours ?? CORAL, 1.0);
      }
    } else {
      const m = add(tube(pts, thick, d.kind === 'cuban' ? 4 : 6));
      if (d.kind === 'cuban') m.material = Object.assign(mt.clone(), { flatShading: true }); // (chunky facets read as links)
    }
    if (d.kind === 'pendant') { const p = frontOf(pts); const k = add(new THREE.CylinderGeometry(0.008, 0.008, 0.0025, 18)); k.position.set(p.x, p.y - 0.011, p.z + 0.003); k.rotation.x = Math.PI / 2 - 0.2; }
    return { g, at: C };
  }
  if (slot === 'ears' || slot === 'nose') {
    const e = f.eye;
    if (!e) return null;
    const ey = (e.min.y + e.max.y) / 2, ez = e.max.z, s = f.body;
    if (slot === 'nose') {
      // the left nostril's wing: the most forward skin just beside the tip, a little below it
      let tipY = ey, tipZ = -Infinity;
      for (let i = 0; i < s.bone.length; i++) if (Math.abs(s.pos[i * 3]) < 0.004 && s.pos[i * 3 + 1] < ey && s.pos[i * 3 + 1] > ey - 0.07 && s.pos[i * 3 + 2] > tipZ) { tipZ = s.pos[i * 3 + 2]; tipY = s.pos[i * 3 + 1]; }
      let best = -1, bz = -Infinity;
      for (let i = 0; i < s.bone.length; i++) { const x = s.pos[i * 3], y = s.pos[i * 3 + 1]; if (x > 0.011 && x < 0.017 && y < tipY && y > tipY - 0.014 && s.pos[i * 3 + 2] > bz) { bz = s.pos[i * 3 + 2]; best = i; } }
      if (best < 0) return null;
      const k = add(new THREE.SphereGeometry(0.0018, 10, 8)); k.position.set(s.pos[best * 3] + 0.0008, s.pos[best * 3 + 1], s.pos[best * 3 + 2] + 0.0006);
      return { g, at: k.position.clone() };
    }
    for (const side of [-1, 1]) {
      // the lobe: the lowest of the ear's outermost skin, a hand's width below the eyes and behind them
      let maxX = 0;
      for (let i = 0; i < s.bone.length; i++) { const x = s.pos[i * 3] * side, y = s.pos[i * 3 + 1], z = s.pos[i * 3 + 2]; if (x > 0 && y < ey - 0.02 && y > ey - 0.08 && z < ez - 0.05 && z > ez - 0.14) maxX = Math.max(maxX, x); }
      let lobe = -1, ly = Infinity;
      for (let i = 0; i < s.bone.length; i++) { const x = s.pos[i * 3] * side, y = s.pos[i * 3 + 1], z = s.pos[i * 3 + 2]; if (x > maxX - 0.012 && y < ey - 0.02 && y > ey - 0.08 && z < ez - 0.05 && z > ez - 0.14 && y < ly) { ly = y; lobe = i; } }
      if (lobe < 0) continue;
      const L = new THREE.Vector3(s.pos[lobe * 3], s.pos[lobe * 3 + 1] + 0.006, s.pos[lobe * 3 + 2]);
      if (d.kind === 'studs') {
        const k = add(new THREE.SphereGeometry(0.0026, 10, 8), d.colours ? stone() : mt); k.position.copy(L).add(new THREE.Vector3(side * 0.0012, 0, 0.001));
      } else if (d.kind === 'hoops') {
        const R = d.id.endsWith('-l') ? 0.02 : 0.011;
        const k = add(new THREE.TorusGeometry(R, 0.0013, 6, 28)); k.position.copy(L).add(new THREE.Vector3(side * 0.003, -R + 0.002, 0)); k.rotation.y = Math.PI / 2;
      } else {
        const hook = add(new THREE.CylinderGeometry(0.0007, 0.0007, 0.012, 5)); hook.position.copy(L).add(new THREE.Vector3(side * 0.002, -0.006, 0));
        const drop = add(new THREE.SphereGeometry(0.0045, 10, 8), bead((d.colours ?? CORAL)[0], 0.25)); drop.position.copy(L).add(new THREE.Vector3(side * 0.002, -0.016, 0)); drop.scale.set(1, 1.3, 1);
      }
    }
    return g.children.length ? { g, at: new THREE.Vector3(0, ey - 0.05, ez - 0.09) } : null;
  }
  if (slot === 'wrist') {
    // the first bracelet on the right wrist, the second (or any watch) on the left
    const side = d.kind === 'watch' ? 'L' : nth ? 'L' : 'R';
    const fa = H(`lowerarm02_${side}`), wr = H(`wrist_${side}`);
    if (!fa || !wr) return null;
    const axis = wr.clone().sub(fa).normalize(), C = wr.clone().addScaledVector(axis, -0.018);
    const bones = new Set([f.bones.findIndex((b) => b.name === `lowerarm02_${side}`), f.bones.findIndex((b) => b.name === `wrist_${side}`)]);
    const pts = wrap(f.dressed, C, axis, 0.008, 0.06, 0.003, bones);
    if (pts.length < 8) return null;
    if (d.kind === 'beads') string(g, pts, 0.0034, d.colours ?? AJ, 0.95);
    else if (d.kind === 'bangles') for (let s = 0; s < 3; s++) { const off = axis.clone().multiplyScalar((s - 1) * 0.006); add(tube(pts.map((p) => p.clone().add(off).sub(C).multiplyScalar(1.06 + s * 0.04).add(C)), 0.0013)); }
    else {
      add(tube(pts, 0.0028, 4), d.colours ? bead(d.colours[0], 0.6) : mt); // the strap or bracelet
      // the face on the back of the wrist (out to the side, arms hanging)
      const out = pts.reduce((a, p) => (Math.abs(p.x) > Math.abs(a.x) ? p : a), pts[0]);
      const n = out.clone().sub(C).normalize();
      const face = add(new THREE.CylinderGeometry(0.012, 0.012, 0.004, 24));
      face.position.copy(out).addScaledVector(n, 0.002); face.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), n);
      const glass = new THREE.Mesh(new THREE.CircleGeometry(0.0095, 24), bead(0x1a1c22, 0.1)); glass.position.set(0, 0.0021, 0); glass.rotation.x = -Math.PI / 2; face.add(glass);
    }
    return { g, at: C };
  }
  if (slot === 'hand') {
    // the ring finger (finger4), left hand first, then the right; a signet on the little finger
    const side = nth ? 'R' : 'L', fing = d.kind === 'signet' ? 'finger5' : 'finger4';
    const a = H(`${fing}-1_${side}`), b = H(`${fing}-2_${side}`);
    if (!a || !b) return null;
    const axis = b.clone().sub(a), len = axis.length(); axis.normalize();
    const bi = f.bones.findIndex((x) => x.name === `${fing}-1_${side}`);
    const rings = d.kind === 'stack' ? [0.3, 0.45, 0.6] : [0.45];
    for (const t of rings) {
      const C = a.clone().addScaledVector(axis, len * t);
      const pts = wrap(f.body, C, axis, 0.004, 0.02, 0.0012, new Set([bi]));
      if (pts.length < 8) continue;
      add(tube(pts, d.kind === 'stack' ? 0.0008 : 0.0012));
      if (d.kind === 'signet' || d.kind === 'solitaire') {
        // on the back of the finger: the side away from the palm (the hand hangs palm-in, so: out from the body)
        const top = pts.reduce((p, q) => (Math.abs(q.x) > Math.abs(p.x) ? q : p), pts[0]), n = top.clone().sub(C).normalize();
        const k = d.kind === 'signet' ? add(new THREE.CylinderGeometry(0.004, 0.004, 0.002, 14)) : add(new THREE.OctahedronGeometry(0.0028), stone());
        k.position.copy(top).addScaledVector(n, 0.0016); k.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), n);
      }
    }
    return g.children.length ? { g, at: a.clone().addScaledVector(axis, len * 0.45) } : null;
  }
  // waist beads: round the hips, low, under the clothes (the body only)
  const sp = H('spine05'), pl = H('pelvis_L'), pr = H('pelvis_R');
  if (!sp || !pl || !pr) return null;
  const C = new THREE.Vector3((pl.x + pr.x) / 2, sp.y + 0.03, sp.z);
  const pts = wrap(f.body, C, new THREE.Vector3(0, 1, 0), 0.008, 0.25, 0.002);
  if (pts.length < 8) return null;
  string(g, pts, 0.0028, d.colours ?? AJ, 1.0);
  return { g, at: C };
}

/** Wear these (jewellery ids) on a skinned body; whatever was worn before comes off. Returns how many went on. */
export function wearJewellery(root: THREE.Object3D, ids: string[]): number {
  const old: THREE.Object3D[] = [];
  root.traverse((o) => { if (o.userData.jewel) old.push(o); });
  for (const o of old) { o.removeFromParent(); o.traverse((k) => { const m = k as THREE.Mesh; if (m.isMesh) { m.geometry.dispose(); if (!([...mats.values()] as THREE.Material[]).includes(m.material as THREE.Material)) (m.material as THREE.Material).dispose(); } }); }
  if (!ids.length) return 0;
  const f = fitOf(root);
  if (!f) return 0;
  const count: Partial<Record<JewelSlot, number>> = {};
  let n = 0;
  for (const id of ids) {
    const d = jewelById(id);
    if (!d) continue;
    const slot = SLOT_OF[d.kind], nth = count[slot] ?? 0;
    count[slot] = nth + 1;
    const built = build(d, f, d.kind === 'watch' ? 1 : nth);
    if (!built) continue;
    // it rides the bone that carries the skin nearest to it
    let best = 0, bd = Infinity;
    const P = f.body.pos;
    for (let i = 0; i < f.body.bone.length; i++) { const dx = P[i * 3] - built.at.x, dy = P[i * 3 + 1] - built.at.y, dz = P[i * 3 + 2] - built.at.z, q = dx * dx + dy * dy + dz * dz; if (q < bd) { bd = q; best = i; } }
    const bi = slot === 'ears' || slot === 'nose' ? Math.max(0, f.bones.findIndex((b) => b.name === 'head')) : f.body.bone[best];
    const g = built.g;
    g.applyMatrix4(f.inv[bi]); // (rest-pose metres into the bone's own space)
    g.traverse((o) => { o.castShadow = false; o.frustumCulled = false; });
    g.userData.jewel = id;
    f.bones[bi].add(g);
    n++;
  }
  return n;
}
